import React, { useEffect, useState } from "react";
import axios from "axios";
import { useVipStore } from "../context/VipContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function VipDutyHistory() {
  const { selectedVip } = useVipStore();
  const [expandedDutyId, setExpandedDutyId] = useState(null);

  const [allData, setAllData] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 20;
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Responsive handler
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowWidth <= 991;

  // Fetch data on mount
  useEffect(() => {
    if (selectedVip?.id) {
      fetchDutyHistory(selectedVip.id);
      return;
    }

    const storedVip = localStorage.getItem("selectedVip");
    if (storedVip) {
      const vipObj = JSON.parse(storedVip);
      if (vipObj?.id) {
        fetchDutyHistory(vipObj.id);
      }
    }
  }, []);

  const fetchDutyHistory = async (vipId) => {
    try {
      setLoading(true);
      const token =
        localStorage.getItem("vipToken") || localStorage.getItem("token");

      const res = await axios.get(
        `${BASE_URL}/api/assignments/vip/${vipId}/history`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = res.data;
      if (Array.isArray(data)) {
        setAllData(data);
      } else if (data && typeof data === "object") {
        setAllData([data]);
      } else {
        setAllData([]);
      }
    } catch (err) {
      console.log("VIP Duty History Error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Search filter
  const filteredData = allData.filter((item) =>
    `
    ${item.id}
    ${item.category?.name || ""}
    ${item.category?.designation || ""}
    
${item.officers?.map(o => o.name).join(" ") || ""}
${item.officers?.map(o => o.rank).join(" ") || ""}
    ${item.status || ""}
    `
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  // Pagination
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = filteredData.slice(
    startIndex,
    startIndex + rowsPerPage
  );
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  // Date formatter
  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString();
  };

  return (
    <>
      <div className="duty-history-wrapper">
        {/* Header */}
        <header className="page-header">
          <div className="header-content">
            <div className="header-title">
              <h1>Duty History</h1>
              <p>Complete record of past and current security assignments</p>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="page-main">
          {/* Search Bar */}
          <section className="search-card">
            <div className="search-inner">
              <i className="fas fa-search search-icon"></i>
              <input
                type="text"
                placeholder="Search by duty ID, officer name, rank, status..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                className="search-input"
              />
            </div>
          </section>

          {/* Duty List */}
          <section className="history-card">
            {loading ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading duty history...</p>
              </div>
            ) : paginatedData.length === 0 ? (
              <div className="no-data-card">
                <i className="fas fa-history fa-3x"></i>
                <h3>No duty history found</h3>
              </div>
            ) : (
              <>
                {isMobile ? (
                  /* Mobile Card View */
                  <div className="mobile-duty-list">
                    {paginatedData.map((item) => {


                      const officers = Array.isArray(item.officers)
                        ? item.officers
                        : item.officers
                          ? [item.officers]
                          : [];


                      const isExpanded = expandedDutyId === item.id;

                      return (
                        <div key={item.id} className="mobile-duty-card">
                          <div
                            className="duty-header"
                            onClick={() =>
                              setExpandedDutyId(isExpanded ? null : item.id)
                            }
                          >
                            <div className="duty-main-info">
                              <strong>Duty #{item.id}</strong>
                              <span className={`status-badge ${item.status.toLowerCase()}`}>
                                {item.status}
                              </span>
                            </div>
                            <div className="duty-date">
                              Assigned: {formatDate(item.assignedAt)}
                            </div>
                            <i
                              className={`fas fa-chevron-${isExpanded ? "up" : "down"} expand-icon`}
                            ></i>
                          </div>

                          {isExpanded && (
                            <div className="duty-details">
                              {officers.length === 0 ? (
                                <p className="no-officer">No officers assigned</p>
                              ) : (
                                <div className="officers-list">
                                  {officers.map((o) => (
                                    <div key={o.id} className="officer-item">
                                      <div className="officer-name">
                                        <i className="fas fa-user-shield"></i>
                                        {o.name}
                                      </div>
                                      <div className="officer-rank">{o.rank}</div>
                                      <div className="officer-period">
                                        <span>From:</span> {formatDate(item.startAt)}
                                      </div>
                                      <div className="officer-period">
                                        <span>Till:</span> {formatDate(item.endAt)}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* Desktop Table View */
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Duty ID</th>
                          <th>Assigned At</th>
                          <th>Status</th>
                          <th>Officers ({filteredData.reduce((sum, item) => {
                            const offs = Array.isArray(item.officer)
                              ? item.officer
                              : item.officer
                                ? [item.officer]
                                : [];
                            return sum + offs.length;
                          }, 0)} total)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.map((item) => {

                          const officers = Array.isArray(item.officers)
                            ? item.officers
                            : item.officers
                              ? [item.officers]
                              : [];
                          const isExpanded = expandedDutyId === item.id;

                          return (
                            <React.Fragment key={item.id}>
                              <tr
                                className="main-row"
                                onClick={() =>
                                  setExpandedDutyId(isExpanded ? null : item.id)
                                }
                              >
                                <td><strong>#{item.id}</strong></td>
                                <td>{formatDate(item.assignedAt)}</td>
                                <td>
                                  <span className={`status-badge ${item.status.toLowerCase()}`}>
                                    {item.status}
                                  </span>
                                </td>
                                <td>
                                  {officers.length} officer{officers.length !== 1 ? "s" : ""}
                                  <i className={`fas fa-chevron-${isExpanded ? "up" : "down"} ml-2`}></i>
                                </td>
                              </tr>

                              {isExpanded && (
                                <tr>
                                  <td colSpan={4} className="expanded-cell">
                                    <div className="expanded-content">
                                      {officers.length === 0 ? (
                                        <p className="no-officer">No officers assigned to this duty</p>
                                      ) : (
                                        <table className="inner-table">
                                          <thead>
                                            <tr>
                                              <th>Officer Name</th>
                                              <th>Rank</th>
                                              <th>Start Date</th>
                                              <th>End Date</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {officers.map((o) => (
                                              <tr key={o.id}>
                                                <td><i className="fas fa-user mr-2"></i>{o.name}</td>
                                                <td>{o.rank}</td>
                                                <td>{formatDate(item.startAt)}</td>
                                                <td>{formatDate(item.endAt)}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="pagination">
                    {[...Array(totalPages)].map((_, i) => (
                      <button
                        key={i}
                        className={`page-btn ${currentPage === i + 1 ? "active" : ""}`}
                        onClick={() => setCurrentPage(i + 1)}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        </main>
      </div>

      {/* Light Purple Glassmorphic Theme */}
      <style jsx>{`
        .duty-history-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #e0d4ff 0%, #f0e6ff 100%);
          color: #4c1d95;
          padding: 0;
          width: 100%;
          overflow-x: hidden;
        }

        .page-header {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid rgba(168, 85, 247, 0.4);
          padding: 1.2rem 2rem;
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .header-content {
          max-width: 1400px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .header-title h1 {
          font-size: 1.8rem;
          font-weight: 700;
          color: #9333ea;
          margin: 0;
        }

        .header-title p {
          margin: 0.25rem 0 0;
          opacity: 0.8;
          font-size: 0.9rem;
          color: #6b46c1;
        }

        .page-main {
          padding: 1.5rem 2rem;
          max-width: 1400px;
          margin: 0 auto;
        }

        @media (max-width: 768px) {
          .page-main { padding: 1rem; }
          .page-header { padding: 1rem 1.5rem; }
        }

        .search-card {
          background: rgba(255, 255, 255, 0.75);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          padding: 1.4rem;
          margin-bottom: 1.5rem;
          border: 1px solid rgba(168, 85, 247, 0.5);
          transition: all 0.3s ease;
        }

        .search-inner {
          position: relative;
          display: flex;
          align-items: center;
        }

        .search-icon {
          position: absolute;
          left: 1.2rem;
          color: #a855f7;
          font-size: 1.2rem;
          z-index: 1;
        }

        .search-input {
          width: 100%;
          padding: 1rem 1rem 1rem 3rem;
          border-radius: 12px;
          border: 2px solid #d8b4fe;
          font-size: 1rem;
          background: rgba(255, 255, 255, 0.9);
          transition: all 0.3s ease;
        }

        .search-input:focus {
          outline: none;
          border-color: #a855f7;
          box-shadow: 0 0 0 4px rgba(168, 85, 247, 0.2);
        }

        .history-card {
          background: rgba(255, 255, 255, 0.75);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          padding: 1.8rem;
          border: 1px solid rgba(168, 85, 247, 0.5);
          transition: all 0.3s ease;
        }

        .history-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 15px 35px rgba(168, 85, 247, 0.25);
        }

        .table-container {
          overflow-x: auto;
          border-radius: 12px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        }

        .data-table {
          width: 100%;
          min-width: 800px;
          border-collapse: collapse;
          background: white;
        }

        .data-table thead {
          background: #a855f7;
          color: white;
        }

        .data-table th,
        .data-table td {
          padding: 1.2rem;
          text-align: left;
        }

        .data-table th {
          font-weight: 600;
        }

        .main-row {
          background: white;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .main-row:hover {
          background: rgba(168, 85, 247, 0.15);
        }

        .expanded-cell {
          padding: 0 !important;
          background: #f8f0ff;
        }

        .expanded-content {
          padding: 1.5rem;
        }

        .inner-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 0.5rem;
        }

        .inner-table th {
          background: #e9d5ff;
          color: #581c87;
          padding: 0.8rem;
          font-size: 0.95rem;
        }

        .inner-table td {
          padding: 1rem;
          border-bottom: 1px solid rgba(168, 85, 247, 0.2);
        }

        .status-badge {
          padding: 0.5rem 1rem;
          border-radius: 30px;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .status-badge.active {
          background: #d4edda;
          color: #155724;
        }

        .status-badge.inactive,
        .status-badge.completed {
          background: #f8d7da;
          color: #721c24;
        }

        .status-badge.pending {
          background: #fff3cd;
          color: #856404;
        }

        /* Mobile Styles */
        .mobile-duty-list {
          display: flex;
          flex-direction: column;
          gap: 1.2rem;
        }

        .mobile-duty-card {
          background: rgba(255, 255, 255, 0.95);
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 8px 25px rgba(168, 85, 247, 0.2);
          border-left: 5px solid #a855f7;
        }

        .duty-header {
          padding: 1.4rem;
          background: white;
          display: flex;
          flex-direction: column;
          gap: 0.8rem;
          cursor: pointer;
          position: relative;
        }

        .duty-main-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .duty-date {
          font-size: 0.95rem;
          opacity: 0.8;
          color: #6b46c1;
        }

        .expand-icon {
          position: absolute;
          right: 1.4rem;
          top: 1.6rem;
          color: #a855f7;
          font-size: 1.1rem;
        }

        .duty-details {
          padding: 1.4rem;
          background: #f8f0ff;
          border-top: 1px solid rgba(168, 85, 247, 0.3);
        }

        .officers-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .officer-item {
          background: white;
          padding: 1rem;
          border-radius: 12px;
          border: 1px solid rgba(168, 85, 247, 0.3);
        }

        .officer-name {
          font-weight: 600;
          color: #581c87;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .officer-rank {
          font-weight: 600;
          color: #9333ea;
          margin-bottom: 0.8rem;
        }

        .officer-period {
          font-size: 0.95rem;
          color: #4c1d95;
        }

        .officer-period span {
          opacity: 0.7;
          margin-right: 0.5rem;
        }

        .no-officer {
          text-align: center;
          padding: 2rem;
          color: #777;
          font-style: italic;
        }

        .pagination {
          display: flex;
          justify-content: center;
          gap: 0.8rem;
          margin-top: 2rem;
          flex-wrap: wrap;
        }

        .page-btn {
          padding: 0.6rem 1rem;
          border-radius: 10px;
          border: 1px solid #a855f7;
          background: white;
          color: #9333ea;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .page-btn:hover {
          background: #a855f7;
          color: white;
          transform: translateY(-2px);
        }

        .page-btn.active {
          background: #9333ea;
          color: white;
          box-shadow: 0 4px 15px rgba(168, 85, 247, 0.4);
        }

        .loading-container {
          text-align: center;
          padding: 4rem;
          color: #9333ea;
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #e9d5ff;
          border-top: 4px solid #a855f7;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .no-data-card {
          text-align: center;
          padding: 4rem;
          color: #9333ea;
        }

        .no-data-card i {
          margin-bottom: 1rem;
          opacity: 0.6;
        }

        .no-data-card h3 {
          font-size: 1.4rem;
          margin: 0;
        }

        .ml-2 { margin-left: 0.5rem; }
        .mr-2 { margin-right: 0.5rem; }
      `}</style>
    </>
  );
}

// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import { useVipStore } from "../context/VipContext";

// const BASE_URL = process.env.REACT_APP_BASE_URL;

// export default function VipDutyHistory() {
//   const { selectedVip } = useVipStore();
//   const [expandedDutyId, setExpandedDutyId] = useState(null);

//   const [allData, setAllData] = useState([]);
//   const [search, setSearch] = useState("");
//   const [currentPage, setCurrentPage] = useState(1);
//   const rowsPerPage = 20;
//   const [loading, setLoading] = useState(true);

//   //  ALWAYS CALL API ON PAGE LOAD
//   useEffect(() => {
//     if (selectedVip?.id) {
//       fetchDutyHistory(selectedVip.id);
//       return;
//     }

//     const storedVip = localStorage.getItem("selectedVip");
//     if (storedVip) {
//       const vipObj = JSON.parse(storedVip);
//       if (vipObj?.id) {
//         fetchDutyHistory(vipObj.id);
//       }
//     }
//   }, []);

//   //  API CALL WITH TOKEN
//   const fetchDutyHistory = async (vipId) => {
//     try {
//       setLoading(true);

//       // 🔑 Adjust token key if needed (e.g. "token")
//       const token =
//         localStorage.getItem("vipToken") || localStorage.getItem("token");

//       const res = await axios.get(
//         `${BASE_URL}/api/assignments/vip/${vipId}/history`,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//           },
//         }
//       );

//       const data = res.data;

//       // 🔥 FORCE ARRAY SHAPE
//       if (Array.isArray(data)) {
//         setAllData(data);
//       } else if (data && typeof data === "object") {
//         setAllData([data]);
//       } else {
//         setAllData([]);
//       }
//     } catch (err) {
//       console.log("VIP Duty History Error:", err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   //  SEARCH FILTER (HANDLES NESTED FIELDS)
//   const filteredData = allData.filter((item) =>
//     `
//     ${item.id}
//  ${item.category?.name}
//     ${item.category?.designation}
//     ${item.officer?.name}
//     ${item.officer?.rank}
//     ${item.status}
//     `
//       .toLowerCase()
//       .includes(search.toLowerCase())
//   );

//   //  PAGINATION
//   const startIndex = (currentPage - 1) * rowsPerPage;
//   const paginatedData = filteredData.slice(
//     startIndex,
//     startIndex + rowsPerPage
//   );
//   const totalPages = Math.ceil(filteredData.length / rowsPerPage);

//   //  DATE FORMATTER
//   const formatDate = (date) => {
//     if (!date) return "N/A";
//     return new Date(date).toLocaleString();
//   };

//   return (
//     <div style={styles.container}>
//       <div style={styles.title}>Vip Duty History</div>

//       {/* 🔍 SEARCH */}
//       <div style={styles.filterRow}>
//         <div style={styles.filterCard}>
//           <div style={styles.filterLabel}>Search Duty</div>
//           <input
//             style={styles.select}
//             placeholder="Search by name, rank, status..."
//             value={search}
//             onChange={(e) => {
//               setSearch(e.target.value);
//               setCurrentPage(1);
//             }}
//           />
//         </div>
//       </div>

//       {/* 📋 TABLE */}
//       <div style={styles.card}>
//         {loading ? (
//           <div style={styles.noData}>Loading duty history...</div>
//         ) : paginatedData.length === 0 ? (
//           <div style={styles.noData}>No duty history found</div>
//         ) : (
//           <table style={styles.table}>
//             <thead>
//               <tr>
//                 <th style={styles.th}>Duty ID</th>
//                 <th style={styles.th}>Assigned At</th>
//                 {/* <th style={styles.th}>Start At</th> */}
//                 <th style={styles.th}>Status</th>
//               </tr>
//             </thead>

//             <tbody>
//               {paginatedData.map((item) => {
//                 const officers = Array.isArray(item.officer)
//                   ? item.officer
//                   : item.officer
//                   ? [item.officer]
//                   : [];

//                 const isExpanded = expandedDutyId === item.id;

//                 return (
//                   <React.Fragment key={item.id}>
//                     {/* 🔹 MAIN ROW */}
//                     <tr
//                       style={styles.row}
//                       onClick={() =>
//                         setExpandedDutyId(isExpanded ? null : item.id)
//                       }
//                     >
//                       <td style={styles.td}>{item.id}</td>
//                       <td style={styles.td}>{formatDate(item.assignedAt)}</td>
//                       {/* <td style={styles.td}>{formatDate(item.startAt)}</td> */}
//                       <td style={styles.td}>
//                         <span
//                           style={
//                             item.status === "Active"
//                               ? styles.statusActive
//                               : styles.statusInactive
//                           }
//                         >
//                           {item.status}
//                         </span>
//                       </td>
//                     </tr>

//                     {/* 🔹 EXPANDED OFFICER TABLE */}
//                     {isExpanded && (
//                       <tr>
//                         <td colSpan={3} style={{ padding: 0 }}>
//                           <table
//                             style={{
//                               width: "100%",
//                               background: "#fafafa",
//                               borderTop: "2px solid #ddd",
//                             }}
//                           >
//                             <thead>
//                               <tr>
//                                 <th style={styles.subTh}>Officer Name</th>
//                                 <th style={styles.subTh}>Rank</th>
//                                 <th style={styles.subTh}>Start At</th>
//                                 <th style={styles.subTh}>End At</th>
//                               </tr>
//                             </thead>

//                             <tbody>
//                               {officers.length === 0 ? (
//                                 <tr>
//                                   <td colSpan={5} style={styles.noOfficer}>
//                                     No officers assigned
//                                   </td>
//                                 </tr>
//                               ) : (
//                                 officers.map((o) => (
//                                   <tr key={o.id}>
//                                     <td style={styles.subTd}>{o.name}</td>
//                                     <td style={styles.subTd}>{o.rank}</td>
//                                     <td style={styles.subTd}>
//                                       {formatDate(item.startAt)}
//                                     </td>
//                                     <td style={styles.subTd}>
//                                       {formatDate(item.endAt)}
//                                     </td>
//                                   </tr>
//                                 ))
//                               )}
//                             </tbody>
//                           </table>
//                         </td>
//                       </tr>
//                     )}
//                   </React.Fragment>
//                 );
//               })}
//             </tbody>
//           </table>
//         )}

//         {/*  PAGINATION */}
//         {totalPages > 1 && (
//           <div style={styles.paginationContainer}>
//             {[...Array(totalPages)].map((_, i) => (
//               <button
//                 key={i}
//                 style={{
//                   ...styles.pageBtn,
//                   ...(currentPage === i + 1 ? styles.activePage : {}),
//                 }}
//                 onClick={() => setCurrentPage(i + 1)}
//               >
//                 {i + 1}
//               </button>
//             ))}
//           </div>
//         )}
//       </div>
//     </div>
//   );
// }

// /* ------------------ UI STYLES ------------------ */

// const styles = {
//   container: { padding: 30, background: "#fff" },
//   title: { fontSize: 30, fontWeight: 800, marginBottom: 25, color: "#1967d2" },

//   filterRow: { display: "flex", gap: 20, marginBottom: 25 },
//   filterCard: {
//     flex: 1,
//     padding: 18,
//     borderRadius: 12,
//     background: "#fff",
//     boxShadow: "0 3px 12px rgba(0,0,0,0.08)",
//   },
//   filterLabel: { fontSize: 15, fontWeight: 600, marginBottom: 6 },
//   select: {
//     width: "100%",
//     padding: "12px",
//     borderRadius: 8,
//     border: "1.5px solid #1a73e8",
//     fontSize: 15,
//   },

//   card: {
//     background: "#fff",
//     padding: 10,
//     borderRadius: 15,
//     boxShadow: "0 6px 25px rgba(0,0,0,0.1)",
//   },
//   table: { width: "100%", borderCollapse: "collapse" },
//   th: {
//     background: "#f4f4f6",
//     padding: "16px 10px",
//     textAlign: "left",
//     borderBottom: "2px solid #ddd",
//     fontWeight: 700,
//   },
//   row: { height: 62, transition: "0.25s", cursor: "pointer" },
//   td: { padding: "14px 10px", borderBottom: "1px solid #eee" },

//   statusActive: {
//     background: "#e6ffe6",
//     padding: "6px 12px",
//     borderRadius: 20,
//     color: "#2e7d32",
//     fontWeight: 600,
//   },
//   statusInactive: {
//     background: "#ffe6e6",
//     padding: "6px 12px",
//     borderRadius: 20,
//     color: "#d32f2f",
//     fontWeight: 600,
//   },
//   statusOther: {
//     background: "#fff8e1",
//     padding: "6px 12px",
//     borderRadius: 20,
//     color: "#ff9800",
//     fontWeight: 600,
//   },

//   noData: {
//     textAlign: "center",
//     padding: 40,
//     fontSize: 18,
//     color: "#777",
//   },

//   paginationContainer: {
//     display: "flex",
//     justifyContent: "center",
//     gap: 10,
//     padding: 15,
//   },
//   pageBtn: {
//     padding: "6px 14px",
//     borderRadius: 6,
//     border: "1px solid #1967d2",
//     background: "white",
//     cursor: "pointer",
//   },
//   activePage: {
//     background: "#1967d2",
//     color: "#fff",
//     fontWeight: 700,
//   },
//   subTh: {
//     background: "#e9eef6",
//     padding: "12px",
//     textAlign: "left",
//     fontWeight: 700,
//     borderBottom: "1px solid #ccc",
//   },

//   subTd: {
//     padding: "10px",
//     borderBottom: "1px solid #eee",
//   },

//   noOfficer: {
//     padding: 20,
//     textAlign: "center",
//     color: "#777",
//     fontSize: 15,
//   },
// };
